import React from 'react';
import { 
  X, FileText, Download, DollarSign, CheckCircle, Link2 
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { 
  PaymentStatusBadge, 
  calculatePaymentStatus, 
  normalizePaymentData 
} from '../utils/paymentUtils';
import { GSTSummary } from './GSTBreakdown';

/**
 * Shared component for viewing Invoice details.
 * Used in AdminInvoiceManagement and AdminPayments.
 */
const InvoiceViewModal = ({ 
  show, 
  onClose, 
  invoice, 
  companySettings, 
  onDownloadPDF,
  onUpdatePayment,
  onCancelInvoice,
  onUpdatePaymentStatus,
  onEditProducts,
  userRole
}) => {
  if (!show || !invoice) return null;

  // Helper functions moved/replicated here for self-containment
  const calculateInvoiceTotal = (items, buyerGST = '') => {
    if (!items || items.length === 0) return 0;
    return items.reduce((sum, item) => {
      const taxableAmount = item.amount || 0;
      const gstAmount = item.gst_amount || 0;
      return sum + (taxableAmount + gstAmount);
    }, 0);
  };

  const isPaymentCompleted = (invoice) => {
    const normalizedData = normalizePaymentData(invoice);
    return calculatePaymentStatus(normalizedData.paidAmount, normalizedData.totalAmount) === 'paid' || 
           calculatePaymentStatus(normalizedData.paidAmount, normalizedData.totalAmount) === 'completed';
  };

  const isRefundCompleted = (invoice) => {
    return invoice.cancellation_payment_status === 'refunded' || invoice.cancellation_payment_status === 'Refunded';
  };

  const hasDiscounts = invoice.items?.some(item => (item.discount || 0) > 0) || false;

  const handleCopyLink = () => {
    if (!invoice.shareToken) {
      toast.error('Share token not available yet. Please reopen this modal.');
      return;
    }
    const guestLink = `${window.location.origin}/view/document/${invoice.shareToken}`;
    navigator.clipboard.writeText(guestLink)
      .then(() => toast.success('Guest link copied to clipboard!'))
      .catch((err) => toast.error('Failed to copy link'));
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="bg-[#0A5F38] p-4 text-white flex justify-between items-center shrink-0">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <FileText className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Invoice Details</h2>
              <p className="text-green-100 text-sm opacity-90">#{invoice.invoice_number?.replace(/-CANCEL-\d+/g, ' (Cancelled)')}</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-8 space-y-8 scrollbar-thin scrollbar-thumb-slate-200">
          {/* Top Section: Company & Customer Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Vendor Details */}
            <div className="bg-slate-50 p-6 rounded-xl border border-slate-100 italic">
              <h3 className="text-[#0A5F38] font-bold text-lg mb-4 border-b border-green-100 pb-2">FROM</h3>
              <div className="space-y-1 text-slate-700">
                <p className="font-bold text-slate-900">{companySettings?.companyName}</p>
                <p className="text-sm">{companySettings?.addressLine1}</p>
                <p className="text-sm">{companySettings?.addressLine2}</p>
                <p className="text-sm">{companySettings?.city}, {companySettings?.state} - {companySettings?.pincode}</p>
                <p className="text-sm mt-2 font-medium">GST: {companySettings?.gstNumber}</p>
                <p className="text-sm font-medium">Contact: {companySettings?.contactNumber}</p>
              </div>
            </div>

            {/* Customer Details */}
            <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm italic">
              <h3 className="text-amber-700 font-bold text-lg mb-4 border-b border-amber-100 pb-2">BILL TO</h3>
              <div className="space-y-1 text-slate-700">
                <p className="font-bold text-slate-900">{invoice.customer_name || 'Walk-in Customer'}</p>
                <p className="text-sm">{invoice.customer_address || 'N/A'}</p>
                <p className="text-sm font-medium mt-2">Phone: {invoice.customer_phone || 'N/A'}</p>
                <p className="text-sm font-medium">Email: {invoice.customer_email || 'N/A'}</p>
                <p className="text-sm font-medium">GST: {invoice.customer_gst || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Document Info */}
          <div className="flex flex-wrap items-center gap-4 bg-slate-50 p-4 rounded-lg border border-slate-100">
            <div className="flex items-center space-x-2">
              <span className="text-slate-500 text-sm font-medium">DATE:</span>
              <span className="text-slate-900 font-bold">{new Date(invoice.date).toLocaleDateString('en-IN')}</span>
            </div>
            <div className="h-4 w-px bg-slate-300 hidden sm:block"></div>
            <div className="flex items-center space-x-2">
              <span className="text-slate-500 text-sm font-medium">STATUS:</span>
              <PaymentStatusBadge paidAmount={invoice.amount_received} totalAmount={calculateInvoiceTotal(invoice.items || [])} />
            </div>
          </div>

          {/* Cancellation Info (If Cancelled) */}
          {(invoice.cancellation_status === 'cancelled' || invoice.status === 'cancelled') && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-red-100 rounded-lg text-red-600">
                  <X className="h-5 w-5" />
                </div>
                <h3 className="text-red-900 font-bold text-lg">Cancellation Details</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div className="flex flex-col">
                  <span className="text-red-700 font-semibold mb-1">Reason:</span>
                  <p className="text-red-900 bg-white/50 p-2 rounded border border-red-100 italic">
                    {invoice.cancellation_reason || 'No reason provided'}
                  </p>
                </div>
                <div className="flex flex-col">
                  <span className="text-red-700 font-semibold mb-1">Payment Status:</span>
                  <p className={`px-3 py-1 rounded-full w-fit font-bold ${
                    isRefundCompleted(invoice) ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                  }`}>
                    {invoice.cancellation_payment_status || 'Pending'}
                  </p>
                </div>
                {invoice.cancelled_at && (
                  <div className="flex items-center space-x-2">
                    <span className="text-red-700 font-semibold">Cancelled On:</span>
                    <span className="text-red-900">{new Date(invoice.cancelled_at).toLocaleDateString('en-IN')}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Items Table */}
          <div className="border border-black overflow-hidden rounded-lg shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#0A5F38] text-white">
                  <th className="p-3 text-left border-r border-green-800 uppercase font-bold tracking-wider">S.No</th>
                  <th className="p-3 text-left border-r border-green-800 uppercase font-bold tracking-wider">Description</th>
                  <th className="p-3 text-center border-r border-green-800 uppercase font-bold tracking-wider">HSN</th>
                  <th className="p-3 text-center border-r border-green-800 uppercase font-bold tracking-wider">QTY</th>
                  <th className="p-3 text-center border-r border-green-800 uppercase font-bold tracking-wider">Rate</th>
                  <th className="p-3 text-center border-r border-green-800 uppercase font-bold tracking-wider">Unit</th>
                  {hasDiscounts && (
                    <th className="p-3 text-center border-r border-green-800 uppercase font-bold tracking-wider">Disc %</th>
                  )}
                  <th className="p-3 text-right border-r border-green-800 uppercase font-bold tracking-wider">Taxable</th>
                  <th className="p-3 text-center border-r border-green-800 uppercase font-bold tracking-wider">GST %</th>
                  <th className="p-3 text-right border-r border-green-800 uppercase font-bold tracking-wider">GST Amt</th>
                  <th className="p-3 text-right uppercase font-bold tracking-wider">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black/10">
                {invoice.items?.map((item, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                    <td className="p-3 border-r border-black/10 text-center">{idx + 1}</td>
                    <td className="p-3 border-r border-black/10 font-medium text-slate-900">{item.product_name}</td>
                    <td className="p-3 border-r border-black/10 text-center text-slate-600">{item.hsn_code || '-'}</td>
                    <td className="p-3 border-r border-black/10 text-center font-bold text-slate-900">{item.quantity}</td>
                    <td className="p-3 border-r border-black/10 text-center text-slate-600">₹{item.unit_price?.toLocaleString('en-IN', { minimumFractionDigits: 1 })}</td>
                    <td className="p-3 border-r border-black/10 text-center text-slate-500">{item.unit_type}</td>
                    {hasDiscounts && (
                      <td className="p-3 border-r border-black/10 text-center text-blue-600 font-medium">{item.discount || 0}%</td>
                    )}
                    <td className="p-3 border-r border-black/10 text-right font-medium">₹{(item.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 1 })}</td>
                    <td className="p-3 border-r border-black/10 text-center text-slate-600">{item.gst_rate}%</td>
                    <td className="p-3 border-r border-black/10 text-right text-slate-600">₹{(item.gst_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 1 })}</td>
                    <td className="p-3 text-right font-bold text-slate-900">₹{(item.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 1 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-6">
            {/* Bank Details */}
            <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
              <h4 className="text-[#0A5F38] font-bold mb-4 flex items-center">
                <CheckCircle className="h-4 w-4 mr-2" />
                BANK DETAILS
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm italic text-slate-700">
                <span className="font-semibold">Bank Name:</span>
                <span>{companySettings?.bankName || 'N/A'}</span>
                <span className="font-semibold">A/C Number:</span>
                <span className="font-bold text-slate-900">{companySettings?.accountNumber || 'N/A'}</span>
                <span className="font-semibold">IFSC Code:</span>
                <span className="font-bold text-slate-900">{companySettings?.ifscCode || 'N/A'}</span>
                <span className="font-semibold">Branch:</span>
                <span>{companySettings?.branchName || 'N/A'}</span>
              </div>
            </div>

            {/* GST Summary */}
            <div className="space-y-4">
              <div className="bg-slate-50 p-6 rounded-xl border-2 border-[#0A5F38]/20 shadow-sm">
                <GSTSummary 
                  items={invoice.items || []}
                  buyerGST={invoice.customer_gst || ''}
                  companySettings={companySettings}
                  subtotal={calculateInvoiceTotal(invoice.items || [], '', false)}
                  className="space-y-2"
                />
                
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <div className="flex justify-between items-end">
                    <div>
                      <span className="text-slate-500 text-sm font-bold block uppercase tracking-wider">Total Received</span>
                      <span className="text-green-600 font-bold text-xl">₹{(invoice.amount_received || 0).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-slate-500 text-sm font-bold block uppercase tracking-wider">Outstanding Balance</span>
                      <span className="text-red-600 font-bold text-2xl">₹{(calculateInvoiceTotal(invoice.items || []) - (invoice.amount_received || 0)).toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Terms & Conditions & Material Description */}
          <div className="space-y-4">
            {invoice.termsAndConditions && (
              <div className="p-6 bg-slate-50 rounded-xl border border-slate-200">
                <h4 className="text-sm font-black text-slate-900 mb-2 uppercase tracking-widest border-b border-slate-300 pb-1 w-fit">Terms & Conditions</h4>
                <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed italic break-words">{invoice.termsAndConditions}</p>
              </div>
            )}
            
            {invoice.materialDescription && (
              <div className="p-6 bg-slate-50 rounded-xl border border-slate-200">
                <h4 className="text-sm font-black text-slate-900 mb-2 uppercase tracking-widest border-b border-slate-300 pb-1 w-fit">Material Description</h4>
                <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed italic break-words">{invoice.materialDescription}</p>
              </div>
            )}
          </div>
        </div>

        {/* Action Toolbar */}
        <div className="bg-slate-100 p-6 border-t border-slate-200 flex flex-wrap justify-end gap-3 shrink-0">
          {invoice.cancellation_status === 'cancelled' || invoice.status === 'cancelled' ? (
            <>
              <div className="px-6 py-2 bg-red-100 text-red-800 rounded-lg font-bold border border-red-200">
                INVOICE CANCELLED
              </div>
              {isRefundCompleted(invoice) ? (
                <div className="px-6 py-2 bg-green-100 text-green-800 rounded-lg font-bold border border-green-200 flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4" />
                  <span>REFUND COMPLETED</span>
                </div>
              ) : (
                onUpdatePaymentStatus && (
                  <button
                    onClick={onUpdatePaymentStatus}
                    className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-all font-bold shadow-md hover:shadow-lg flex items-center space-x-2"
                  >
                    <DollarSign className="h-4 w-4" />
                    <span>UPDATE PAYMENT STATUS</span>
                  </button>
                )
              )}
            </>
          ) : (
            <>
              {/* Only show Admin actions if context allows */}
              {userRole === 'admin' && onEditProducts && (
                <button
                  onClick={onEditProducts}
                  className="px-6 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-900 transition-all font-bold shadow-md hover:shadow-lg flex items-center space-x-2"
                >
                  <FileText className="h-4 w-4" />
                  <span>EDIT PRODUCTS</span>
                </button>
              )}
              
              {!isPaymentCompleted(invoice) && onUpdatePayment && (
                <button
                  onClick={() => onUpdatePayment(invoice)}
                  className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-all font-bold shadow-md hover:shadow-lg"
                >
                  UPDATE PAYMENT
                </button>
              )}
              
              {isPaymentCompleted(invoice) && (
                <div className="px-6 py-2 bg-green-100 text-green-800 rounded-lg font-bold border border-green-200">
                  PAYMENT COMPLETED
                </div>
              )}
              
              {onCancelInvoice && (
                <button
                  onClick={onCancelInvoice}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-bold shadow-md"
                >
                  CANCEL INVOICE
                </button>
              )}
            </>
          )}

          <button
            onClick={handleCopyLink}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all font-bold shadow-md flex items-center space-x-2"
          >
            <Link2 className="h-4 w-4" />
            <span>COPY GUEST LINK</span>
          </button>

          <button
            onClick={onDownloadPDF}
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all font-bold shadow-md flex items-center space-x-2"
          >
            <Download className="h-4 w-4" />
            <span>DOWNLOAD PDF</span>
          </button>
          
          <button
            onClick={onClose}
            className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-white transition-all font-bold"
          >
            CLOSE
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceViewModal;
